import { engine } from '../audio/engine';
import { getSliceBuffer, SLICE_XFADE } from '../audio/registry';
import type { SourceMeta, Step } from '../types';

/** Plays a single slice through the preview bus, honouring step tweaks. */
export function auditionSlice(source: SourceMeta, slice: number, step?: Step): void {
  const audio = getSliceBuffer({
    source,
    slice,
    reverse: step?.reverse ?? false,
    pitch: 0,
    stretch: 1,
  });
  if (!audio) return;
  const semis = (step?.pitch ?? 0) + source.rootPitch;
  const rate = Math.pow(2, semis / 12) * (step?.rate ?? 1);
  engine.preview({
    buffer: audio.buffer,
    offset: 0,
    duration: audio.buffer.duration / rate,
    fadeIn: audio.preroll / rate,
    fadeOut: SLICE_XFADE,
    playbackRate: rate,
    gain: (step?.gain ?? 1) * source.gain,
    pan: step?.pan ?? 0,
    filter: step?.filter ?? 'off',
    cutoff: step?.cutoff ?? 20000,
    resonance: step?.resonance ?? 1,
    step: slice,
  });
}

/** Rolling-average tap tempo. Resets after two seconds of silence. */
export class TapTempo {
  private taps: number[] = [];

  tap(): number | null {
    const now = performance.now();
    if (this.taps.length && now - this.taps[this.taps.length - 1] > 2000) this.taps = [];
    this.taps.push(now);
    if (this.taps.length < 2) return null;
    this.taps = this.taps.slice(-8);
    const deltas: number[] = [];
    for (let i = 1; i < this.taps.length; i++) deltas.push(this.taps[i] - this.taps[i - 1]);
    const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length;
    return Math.round((60000 / avg) * 10) / 10;
  }

  reset(): void {
    this.taps = [];
  }
}
